import { useLocalStorageState } from "ahooks";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

import { forwardRef, useEffect, useMemo, useState } from "react";
import { createSearchParams } from "react-router-dom";

import CloseIcon from "@mui/icons-material/Close";
import ConnectingAirportsIcon from "@mui/icons-material/ConnectingAirports";
import SentimentVerySatisfiedIcon from "@mui/icons-material/SentimentVerySatisfied";
import TableRowsRoundedIcon from "@mui/icons-material/TableRowsRounded";
import ViewHeadlineIcon from "@mui/icons-material/ViewHeadline";
import { Button, Link, ToggleButton, ToggleButtonGroup } from "@mui/material";
import AppBar from "@mui/material/AppBar";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Slide from "@mui/material/Slide";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { TransitionProps } from "@mui/material/transitions";

import {
  ApiFlightRoutesSampleData,
  ApiGetFlightRoutesSampleParams,
  getFlightRoutesRefresh,
  getFlightRoutesSample,
} from "@/api/routes";
import FlightItem from "@/components/FlightItem";
import FlightListSkeleton from "@/components/FlightList/Skeleton";
import FlightGridItem from "@/components/FlightListGrid/FlightItem";
import OptionDialog from "@/components/OptionDialog";
import CabinSort, { CabinSortMap } from "@/components/Sort/CabinSort";
import { sortFn } from "@/components/Sort/sortConfig";
import { AIRPORT_MAP } from "@/constants/airport";
import { HiddenRealTimeSearchProgram } from "@/constants/config";
import useEvent from "@/hooks/useEvent";
import usePlatform from "@/hooks/usePlatform";
import { SearchConditionType } from "@/hooks/useSearchCondition";
import useThemeData from "@/hooks/useThemeData";
import { CabinsType, FlightItem as FlightItemType } from "@/typings";
import { shouldShowJustNow } from "@/utils";
import { createSearchQuery } from "@/utils/searchQuery";

dayjs.extend(customParseFormat);

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface FlightDetailsProps {
  open: boolean;
  onClose: () => void;
  params?: ApiGetFlightRoutesSampleParams & { type: CabinsType };
  whiteList?: string[];
}

const ListViewType = {
  List: 1,
  Grid: 2,
};

export default function FlightDetails(props: FlightDetailsProps) {
  const { open, onClose, params, whiteList } = props;
  const theme = useThemeData();

  const [data, setData] = useState<ApiFlightRoutesSampleData>();

  const [viewType, setViewType] = useLocalStorageState(
    "__panoramaFlightDetailViewTypeTooltipIdentify__",
    {
      defaultValue: ListViewType.Grid,
    }
  );

  const [alignment, setAlignment] = useState<CabinsType>(params?.type || "Economy");
  const [expandKeys, setExpandKey] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshLoading, setRefreshLoading] = useState(false);

  const [optionDialogData, setOptionDialogData] = useState<FlightItemType>();
  const { isMobile } = usePlatform();
  const [sortKey, setSortKey] = useState(CabinSortMap[params?.type || "Economy"].asc);
  const handleChange = (event: React.MouseEvent<HTMLElement>, newAlignment: CabinsType) => {
    setAlignment(newAlignment);
  };

  const handleExpandChange = useEvent((key: string) => {
    if (expandKeys.includes(key)) {
      setExpandKey(expandKeys.filter((ele) => ele !== key));
    } else {
      setExpandKey([...expandKeys, key]);
    }
  });

  const { isPc } = usePlatform();
  const goSearch = (targetId?: string) => {
    if (params) {
      console.log("%c [ params ]-87", "font-size:13px; background:pink; color:#bf2c9f;", params);
      // TODO: use better code style to convert date string to timestamp

      const year = dayjs(params?.date).year();
      const month = dayjs(params?.date).month();
      const day = dayjs(params?.date).date();

      const epochTimestamp = new Date(year, month, day).getTime(); // Convert to seconds

      const condition: SearchConditionType = {
        flightWay: "oneway",
        pax: 1,
        cabins: alignment,
        range: true,

        fromLocation: [AIRPORT_MAP[params?.origin]],
        toLocation: [AIRPORT_MAP[params?.arrival]],

        oneWayRangeStartDate: epochTimestamp,
        oneWayRangeEndDate: epochTimestamp,
      };

      if (targetId) {
        Object.assign(condition, { targetId });
      }

      const params2 = createSearchQuery(condition);

      const search = createSearchParams(params2).toString();

      // navigate({
      //   pathname: "/flight",
      //   search: search,
      // });

      window.open(`/flight?${search}`, "_blank");
    }
  };
  const fetchData = useEvent(async () => {
    if (props.params) {
      try {
        setLoading(true);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { type, ...restParams } = props.params;
        const res = await getFlightRoutesSample({
          ...restParams,
          date: dayjs(restParams.date).format("YYYYMMDD"),
        });

        setData(res);
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    }
  });

  const refreshData = useEvent(async () => {
    if (!canRefresh) {
      console.log("can't refresh");
      return;
    }
    if (props.params) {
      try {
        setRefreshLoading(true);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { type, ...restParams } = props.params;
        const res = await getFlightRoutesRefresh({
          pax: 1,
          programs: [restParams.program],
          departure_airports: [restParams.origin],
          arrival_airports: [restParams.arrival],
          date_range: {
            from: restParams.date,
            to: restParams.date,
          },
        });
        setData(res);
        setRefreshLoading(false);
      } catch (error) {
        setRefreshLoading(false);
      }
    }
  });

  useEffect(() => {
    if (open) {
      fetchData();
    } else {
      setData(undefined);
      setAlignment("Economy");
      setLoading(true);
    }
  }, [fetchData, open]);

  const currentList = useMemo(() => {
    if (viewType === ListViewType.List) {
      if (data?.data) {
        return data.data[alignment] || [];
      }

      return [];
    }

    return sortFn(data?.data ? Object.values(data?.data).flat() : [], sortKey);
  }, [viewType, data, sortKey, alignment]);

  useEffect(() => {
    const shouldUpdate = Object.values(data?.data ?? {}).some((item) =>
      item.some((item) => !shouldShowJustNow(item.ls))
    );
    if (shouldUpdate) {
      refreshData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentList]);

  const data_ct = useMemo(() => {
    if (data?.data_ct) {
      return data.data_ct[alignment] || 0;
    }

    return 0;
  }, [data, alignment]);
  const flight_left = data_ct - currentList.length;

  const hiddenRealTimeSearch = HiddenRealTimeSearchProgram.includes(params?.program || "");

  // const canRefresh = REFRESH_PROGRAM_NAME_MAP.has(params?.program || "");
  const canRefresh = !whiteList?.includes?.(params?.program?.toLocaleLowerCase() || "");

  const ComponentItem = useMemo(() => {
    if (viewType === ListViewType.List) {
      return FlightItem;
    }

    return FlightGridItem;
  }, [viewType]);

  return (
    <>
      <Dialog
        fullScreen={isPc ? true : true}
        open={open}
        onClose={onClose}
        TransitionComponent={Transition}
      >
        <AppBar sx={{ position: "relative" }}>
          <Toolbar>
            <ConnectingAirportsIcon />
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              {params?.origin} - {AIRPORT_MAP[params?.arrival].city}({params?.arrival})
            </Typography>
            <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close">
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        <DialogContent
          sx={{
            padding: "10px",
          }}
        >
          <div className="text-right my-[10px] flex items-center justify-between">
            <ToggleButtonGroup
              size="small"
              value={viewType}
              exclusive
              onChange={(e, v) => {
                if (v) {
                  setViewType(v);
                }
              }}
              aria-label="text alignment"
              sx={{
                ".MuiToggleButton-root": {
                  padding: "3px",
                  color: "primary.main",
                },
                ".Mui-selected": {
                  color: "primary.main",
                },
              }}
            >
              <ToggleButton value={ListViewType.List} aria-label="list">
                <TableRowsRoundedIcon />
              </ToggleButton>

              <ToggleButton value={ListViewType.Grid} aria-label="grid">
                <ViewHeadlineIcon />
              </ToggleButton>
            </ToggleButtonGroup>

            <div className="flex items-center">
              {refreshLoading && (
                <div
                  style={{
                    marginLeft: "10px",
                    marginRight: "20px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <CircularProgress thickness={5} size={30} className="mr-[10px]" />
                  <Typography>
                    {isMobile ? "Refreshing data" : "Refreshing data with airline..."}
                  </Typography>
                </div>
              )}
              {canRefresh && (
                <Button
                  variant="contained"
                  className={isPc ? "w-[320px]" : ""}
                  onClick={refreshData}
                  sx={{ borderRadius: "50px" }}
                >
                  <SentimentVerySatisfiedIcon className="mr-[6px]" />
                  Refresh Data
                </Button>
              )}
            </div>
          </div>

          {viewType === ListViewType.List && (
            <ToggleButtonGroup
              color="primary"
              value={alignment}
              exclusive
              onChange={handleChange}
              aria-label="Platform"
              size="small"
              sx={{
                width: "100%",
                display: "flex",
                marginBottom: "10px",
              }}
            >
              <ToggleButton
                value="Economy"
                sx={{
                  borderTopLeftRadius: "30px",
                  borderBottomLeftRadius: "30px",
                  flex: 1,
                }}
              >
                Economy
              </ToggleButton>
              <ToggleButton
                value="Premium Economy"
                sx={{
                  borderTopLeftRadius: "30px",
                  borderBottomLeftRadius: "30px",
                  flex: 1,
                }}
              >
                {isPc ? "Premium Economy" : "Premium"}
              </ToggleButton>

              <ToggleButton
                value="Business"
                sx={{
                  borderTopLeftRadius: "30px",
                  borderBottomLeftRadius: "30px",
                  flex: 1,
                }}
              >
                Business
              </ToggleButton>

              <ToggleButton
                value="First"
                sx={{
                  borderTopRightRadius: "30px",
                  borderBottomRightRadius: "30px",
                  flex: 1,
                }}
              >
                First
              </ToggleButton>
            </ToggleButtonGroup>
          )}

          {viewType === ListViewType.Grid && currentList.length > 0 && (
            <CabinSort value={sortKey} onChange={setSortKey} dataSource={currentList} />
          )}

          {currentList.length === 0 && !loading ? (
            <div className="mt-[80px] text-center">
              <Typography color="textSecondary">No results match your current filters.</Typography>
              <img src={theme.emptyBg} alt="" className="h-[200px] mt-[30px]" />
            </div>
          ) : (
            <div>
              {currentList.length
                ? currentList.map((item) => {
                    return (
                      <ComponentItem
                        showLastSeen
                        data={item}
                        key={item.hash}
                        isExpanded={expandKeys.includes(item.hash)}
                        onExpandChange={handleExpandChange}
                        onOpenOptionDialog={(value) => {
                          setOptionDialogData(value);
                        }}
                        showSearchBtn={!hiddenRealTimeSearch}
                        onSearch={() => goSearch(item.id)}
                      />
                    );
                  })
                : null}
              {loading ? <FlightListSkeleton count={currentList.length} /> : null}
            </div>
          )}
          {currentList.length !== 0 && data_ct && currentList.length !== data_ct ? (
            <Typography textAlign="center" className="pt-[10px]">
              {flight_left == 1
                ? "1 more flight was seen"
                : "+" + flight_left + " more flights were seen"}
            </Typography>
          ) : null}
          {currentList.length !== 0 && !hiddenRealTimeSearch && (
            <Link textAlign="center">
              <Typography className="pt-[20px] pb-[40px]">
                <Typography
                  component={Button}
                  color="primary"
                  onClick={() => goSearch()}
                  textAlign="center"
                  className="pt-[10px] pb-[30px]"
                >
                  {"Check with Real-time Search Engine"}
                </Typography>
              </Typography>
            </Link>
          )}
        </DialogContent>
      </Dialog>

      <OptionDialog
        data={optionDialogData}
        onClose={() => {
          setOptionDialogData(undefined);
        }}
      />
    </>
  );
}
